import type { BestBacktestArtifactUniverseMode } from '@einhaus/project314-types';
import FormCheckbox from '@components/UI/FormCheckbox';
import { BEST_BACKTEST_EVIDENCE_MODE_DETAILS } from './portfolioBacktestEvidenceMode';

interface PortfolioBacktestEvidenceModeControlProps {
    affectedStrategyNames: readonly string[];
    hasAcknowledgedReconstruction: boolean;
    mode: BestBacktestArtifactUniverseMode;
    onAcknowledgementChange: (acknowledged: boolean) => void;
    onModeChange: (mode: BestBacktestArtifactUniverseMode) => void;
}

const evidenceModes = ['strict_operational', 'trade_cutoff_reconstruction'] as const;

const PortfolioBacktestEvidenceModeControl = ({
    affectedStrategyNames,
    hasAcknowledgedReconstruction,
    mode,
    onAcknowledgementChange,
    onModeChange
}: PortfolioBacktestEvidenceModeControlProps) => (
    <section aria-labelledby="cohort-evidence-heading" className="mb-6 rounded-lg border border-zinc-700 bg-zinc-900/60 p-4">
        <h5 className="text-lg font-medium text-white" id="cohort-evidence-heading">
            Cohort selection historical evidence
        </h5>
        <p className="mt-1 text-sm text-zinc-400">
            This controls what historical information cohort-based profiles may use. It does not change the stocks or assets in the
            backtest.
        </p>
        <p className="mt-2 text-xs text-zinc-500">
            Applies to: <span className="text-zinc-300">{affectedStrategyNames.join(', ')}</span>
        </p>

        <fieldset className="mt-4 space-y-3">
            <legend className="sr-only">Historical evidence mode</legend>
            {evidenceModes.map((evidenceMode) => {
                const details = BEST_BACKTEST_EVIDENCE_MODE_DETAILS[evidenceMode];
                const isSelected = mode === evidenceMode;

                return (
                    <label
                        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                            isSelected
                                ? evidenceMode === 'trade_cutoff_reconstruction'
                                    ? 'border-amber-600 bg-amber-950/30'
                                    : 'border-blue-500 bg-blue-950/30'
                                : 'border-zinc-700 bg-zinc-950/40 hover:border-zinc-600'
                        }`}
                        key={evidenceMode}
                    >
                        <input
                            checked={isSelected}
                            className="mt-1 h-4 w-4 shrink-0 border-zinc-500 bg-zinc-800 text-blue-600 focus:ring-blue-500"
                            name="backtest-selection-evidence-mode"
                            type="radio"
                            value={evidenceMode}
                            onChange={() => onModeChange(evidenceMode)}
                        />
                        <span>
                            <span className="block text-sm font-medium text-zinc-100">
                                {details.modalLabel}
                                {evidenceMode === 'strict_operational' && (
                                    <span className="ml-2 rounded bg-blue-950 px-2 py-0.5 text-xs font-normal text-blue-200">
                                        Recommended
                                    </span>
                                )}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-zinc-400">{details.description}</span>
                        </span>
                    </label>
                );
            })}
        </fieldset>

        {mode === 'trade_cutoff_reconstruction' && (
            <div className="mt-4 rounded-md border border-amber-700 bg-amber-950/40 p-3 text-amber-100" role="alert">
                <div className="text-sm font-medium">Research-only result</div>
                <p className="mt-1 text-xs leading-5 text-amber-200">
                    This run may evaluate exit or option configurations that did not exist yet on the simulated date. Do not treat the
                    result as historical live parity.
                </p>
                <div className="mt-3">
                    <FormCheckbox
                        checked={hasAcknowledgedReconstruction}
                        id="acknowledge-backtest-reconstruction"
                        label="I understand this run may use configurations created after the simulated date."
                        labelClassName="text-amber-100"
                        onChange={(event) => onAcknowledgementChange(event.target.checked)}
                    />
                </div>
            </div>
        )}
    </section>
);

export default PortfolioBacktestEvidenceModeControl;
